import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiExternalLink, FiGithub, FiCheck } from 'react-icons/fi';
import { useApp } from '../context/AppContext';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { projects } = useApp();
  const navigate = useNavigate();
  const project = projects.find(p => p.id === Number(id));

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-6xl text-white/20 mb-4">Project Not Found</h1>
          <Link to="/projects" className="btn-primary">Back to Projects</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24">
      {/* Back */}
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 font-body text-sm text-white/50 hover:text-white transition-colors"
        >
          <FiArrowLeft /> Back to Projects
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hero image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl overflow-hidden mb-10"
            >
              <img
                src={project.thumbnail}
                alt={project.title}
                className="w-full h-80 md:h-[480px] object-cover"
              />
            </motion.div>

            {/* Title & Category */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <span className="px-3 py-1 rounded-full text-xs font-body"
                style={{ background: 'rgba(127,4,155,0.3)', color: '#c084fc' }}>
                {project.category}
              </span>
              <h1 className="font-display text-5xl font-light text-white mt-4 mb-4">{project.title}</h1>
              <p className="font-body text-white/60 leading-relaxed text-lg mb-8">{project.description}</p>
            </motion.div>

            {/* Features */}
            {project.features && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                className="glass-card p-8 mb-8">
                <h2 className="font-display text-2xl text-white mb-6">Key Features</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(127,4,155,0.3)' }}>
                        <FiCheck size={12} className="text-purple-400" />
                      </span>
                      <span className="font-body text-sm text-white/70">{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Gallery */}
            {project.gallery && project.gallery.length > 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <h2 className="font-display text-2xl text-white mb-6">Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.gallery.map((img, i) => (
                    <div key={i} className="rounded-xl overflow-hidden">
                      <img src={img} alt={`${project.title} ${i + 1}`} className="w-full h-48 object-cover" />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-8 sticky top-28"
            >
              <h3 className="font-body text-xs tracking-widest uppercase text-white/30 mb-6">Project Details</h3>

              {/* Tech Stack */}
              <div className="mb-8">
                <p className="font-body text-sm font-medium text-white mb-3">Technologies</p>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map(t => (
                    <span key={t} className="px-3 py-1 rounded-full text-xs font-mono text-purple-300 border border-purple-500/30">
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="h-px bg-white/5 mb-8" />

              {/* Links */}
              <div className="space-y-3">
                {project.liveUrl && (
                  <a href={project.liveUrl} target="_blank" rel="noopener noreferrer"
                    className="w-full btn-primary flex items-center justify-center gap-2 text-sm">
                    <FiExternalLink /> Visit Live Site
                  </a>
                )}
                {project.githubUrl && (
                  <a href={project.githubUrl} target="_blank" rel="noopener noreferrer"
                    className="w-full btn-outline flex items-center justify-center gap-2 text-sm">
                    <FiGithub /> View on GitHub
                  </a>
                )}
              </div>

              <div className="h-px bg-white/5 my-8" />

              <p className="font-body text-sm text-white/50 mb-4">Like what you see?</p>
              <Link to="/contact" className="w-full btn-primary flex items-center justify-center gap-2 text-sm">
                Start a Similar Project
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
